import React from 'react';
import { theme } from '../styles/theme';
import Container from '../components/ui/Container';
import FooterPageContent from '../components/layout/FooterPageContent';

const ShippingPolicyPage = () => {
  const pageStyles = {
    padding: `${theme.spacing.xxl} 0`,
    backgroundColor: theme.colors.background,
    minHeight: '100vh',
  };

  const contentStyles = {
    maxWidth: '900px',
    margin: '0 auto',
    padding: `0 ${theme.spacing.lg}`,
  };

  const headingStyles = {
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: theme.typography.fontSize.xxl,
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.primary,
    marginBottom: theme.spacing.lg,
    textAlign: 'center',
  };

  const subHeadingStyles = {
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.colors.primaryDark,
    marginTop: theme.spacing.xl,
    marginBottom: theme.spacing.md,
  };

  const sectionHeadingStyles = {
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.colors.primary,
    marginTop: theme.spacing.lg,
    marginBottom: theme.spacing.sm,
  };

  const paragraphStyles = {
    fontSize: theme.typography.fontSize.base,
    lineHeight: theme.typography.lineHeight.relaxed,
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
  };

  const listStyles = {
    fontSize: theme.typography.fontSize.base,
    lineHeight: theme.typography.lineHeight.relaxed,
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
    paddingLeft: theme.spacing.xl,
  };

  const contactBoxStyles = {
    backgroundColor: theme.colors.neutral,
    padding: theme.spacing.lg,
    borderRadius: theme.borderRadius.md,
    marginTop: theme.spacing.xl,
    marginBottom: theme.spacing.lg,
  };

  const highlightBoxStyles = {
    backgroundColor: theme.colors.primaryLight,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    marginBottom: theme.spacing.lg,
    borderLeft: `4px solid ${theme.colors.primary}`,
  };

  const lastUpdatedStyles = {
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginBottom: theme.spacing.xl,
    fontStyle: 'italic',
  };

  return (
    <FooterPageContent slug="shipping">
    <div style={pageStyles}>
      <Container>
        <div style={contentStyles}>
          <h1 style={headingStyles}>Shipping Policy</h1>
          <p style={lastUpdatedStyles}>Last Updated: January 2026</p>

          <p style={paragraphStyles}>
            At BS Trading Co., we strive to process and ship your orders quickly and efficiently. 
            This Shipping Policy outlines our shipping methods, timeframes, costs, and important 
            information about deliveries.
          </p>

          <div style={highlightBoxStyles}>
            <p style={{ ...paragraphStyles, marginBottom: 0 }}>
              <strong>Important:</strong> Shipping times and costs vary based on your location, 
              product type, and order size. All shipping charges are calculated by our team and 
              you will be contacted with the exact shipping cost and estimated delivery time before 
              your order is processed.
            </p>
          </div>

          <h2 style={subHeadingStyles}>1. Processing Time</h2>
          <p style={paragraphStyles}>
            Orders are typically processed within 1-3 business days (Monday through Friday, 
            excluding holidays). Processing time may be longer during peak seasons or for 
            custom orders.
          </p>

          <div style={highlightBoxStyles}>
            <p style={{ ...paragraphStyles, marginBottom: 0 }}>
              <strong>Note:</strong> Processing time is separate from shipping time. Your order 
              will be shipped after processing is complete.
            </p>
          </div>

          <h2 style={subHeadingStyles}>2. Shipping Methods & Delivery Times</h2>
          <p style={paragraphStyles}>
            <strong>Shipping times vary based on your location and the products ordered.</strong> Our 
            team will calculate the most appropriate shipping method and provide you with an estimated 
            delivery timeframe when we contact you about your order.
          </p>

          <p style={paragraphStyles}>
            We work with various carriers to ensure your order arrives safely:
          </p>

          <h3 style={sectionHeadingStyles}>Standard Shipping</h3>
          <ul style={listStyles}>
            <li>Delivery time varies by location</li>
            <li>Available for most products</li>
            <li>Tracking provided</li>
            <li>Our team will provide specific delivery estimates</li>
          </ul>

          <h3 style={sectionHeadingStyles}>Expedited Shipping</h3>
          <ul style={listStyles}>
            <li>Faster delivery available for eligible items</li>
            <li>Delivery time depends on your location</li>
            <li>Additional fees apply</li>
            <li>Contact us to inquire about expedited options</li>
          </ul>

          <h3 style={sectionHeadingStyles}>Freight Shipping</h3>
          <ul style={listStyles}>
            <li>Required for large or heavy items (furniture, bulk orders)</li>
            <li>Delivery time varies significantly by location</li>
            <li>Curbside delivery or liftgate service available</li>
            <li>Our team will provide a custom freight quote and timeline</li>
          </ul>

          <h2 style={subHeadingStyles}>3. Shipping Costs</h2>
          <p style={paragraphStyles}>
            <strong>All shipping costs are calculated by our team</strong> after you place your order. 
            Our employees will review your order and determine the most cost-effective shipping method 
            based on:
          </p>
          <ul style={listStyles}>
            <li>Package weight and dimensions</li>
            <li>Shipping destination</li>
            <li>Most appropriate shipping method for your items</li>
            <li>Product type (standard vs. freight)</li>
            <li>Current carrier rates</li>
          </ul>

          <p style={paragraphStyles}>
            You will be contacted with the exact shipping cost before your order is processed and 
            charged. This ensures you receive accurate pricing and the best shipping option for 
            your specific order.
          </p>

          <h2 style={subHeadingStyles}>4. Shipping Destinations</h2>
          
          <h3 style={sectionHeadingStyles}>Domestic Shipping (United States)</h3>
          <p style={paragraphStyles}>
            We ship to all 50 U.S. states. Additional fees or restrictions may apply for:
          </p>
          <ul style={listStyles}>
            <li>Alaska and Hawaii (extended delivery times and higher costs)</li>
            <li>P.O. Boxes (limited to small packages only)</li>
            <li>Military APO/FPO addresses</li>
          </ul>

          <h3 style={sectionHeadingStyles}>International Shipping</h3>
          <p style={paragraphStyles}>
            International shipping may be available for select products. Please contact us 
            directly to inquire about international orders. Additional customs fees, duties, 
            and taxes are the responsibility of the customer.
          </p>

          <h2 style={subHeadingStyles}>5. Order Tracking</h2>
          <p style={paragraphStyles}>
            Once your order ships, you will receive a shipping confirmation email with tracking 
            information. You can use this tracking number to monitor your package's progress.
          </p>

          <h2 style={subHeadingStyles}>6. Delivery Issues</h2>
          
          <h3 style={sectionHeadingStyles}>Lost or Stolen Packages</h3>
          <p style={paragraphStyles}>
            If your package is marked as delivered but you haven't received it:
          </p>
          <ul style={listStyles}>
            <li>Check with neighbors or household members</li>
            <li>Look around your property (porches, garages, etc.)</li>
            <li>Contact the carrier directly</li>
            <li>Contact us within 7 days for assistance</li>
          </ul>

          <h3 style={sectionHeadingStyles}>Damaged Packages</h3>
          <p style={paragraphStyles}>
            If your package arrives damaged:
          </p>
          <ul style={listStyles}>
            <li>Take photos of the damage (box and contents)</li>
            <li>Keep all packaging materials</li>
            <li>Contact us immediately at Orders@BSTrading.com</li>
            <li>We will work with you to resolve the issue</li>
          </ul>

          <h3 style={sectionHeadingStyles}>Incorrect Address</h3>
          <p style={paragraphStyles}>
            Please ensure your shipping address is correct at checkout. We are not responsible 
            for orders shipped to incorrect addresses provided by the customer. Address changes 
            may be possible if you contact us immediately after placing your order, before it ships.
          </p>

          <h2 style={subHeadingStyles}>7. Special Handling Items</h2>
          <p style={paragraphStyles}>
            Certain products require special handling or packaging:
          </p>
          <ul style={listStyles}>
            <li>Cowhides and large hides - rolled and shipped in protective packaging</li>
            <li>Furniture - may require freight shipping and assembly</li>
            <li>Fragile items - extra packaging and insurance recommended</li>
            <li>Oversized items - freight shipping required</li>
          </ul>

          <h2 style={subHeadingStyles}>8. Wholesale & Bulk Orders</h2>
          <p style={paragraphStyles}>
            Shipping for wholesale and bulk orders is handled on a case-by-case basis. Our team 
            will work with you to find the most cost-effective shipping solution and provide accurate 
            delivery timeframes. Each wholesale order receives personalized attention to ensure the 
            best shipping rates and service for your needs.
          </p>

          <h2 style={subHeadingStyles}>9. Local Pickup</h2>
          <p style={paragraphStyles}>
            Local pickup is available at our Burleson, Texas location by appointment. Select 
            "Local Pickup" at checkout or contact us to arrange pickup.
          </p>

          <div style={highlightBoxStyles}>
            <p style={{ ...paragraphStyles, marginBottom: 0 }}>
              <strong>Pickup Location:</strong> 253 Loy St, Burleson, TX 76028<br />
              <strong>Hours:</strong> Monday - Friday, 8:00 AM - 5:00 PM CST<br />
              <strong>Note:</strong> Please bring your order confirmation and valid ID.
            </p>
          </div>

          <h2 style={subHeadingStyles}>10. Shipping Delays</h2>
          <p style={paragraphStyles}>
            While we strive for timely delivery, delays may occur due to:
          </p>
          <ul style={listStyles}>
            <li>Weather conditions</li>
            <li>Carrier delays</li>
            <li>High order volume during peak seasons</li>
            <li>Customs processing (international orders)</li>
            <li>Incorrect or incomplete address information</li>
          </ul>

          <p style={paragraphStyles}>
            We are not responsible for delays caused by shipping carriers or circumstances 
            beyond our control.
          </p>

          <h2 style={subHeadingStyles}>11. Contact Us</h2>
          <p style={paragraphStyles}>
            If you have questions about shipping or need assistance with your order, please 
            contact us:
          </p>

          <div style={contactBoxStyles}>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>BS Trading Co.</strong>
            </p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>253 Loy St</p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>Burleson, TX 76028</p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>Phone:</strong> <a href="tel:8174474433" style={{ color: theme.colors.primary }}>817-447-4433</a>
            </p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>Toll-Free:</strong> <a href="tel:18005540910" style={{ color: theme.colors.primary }}>1-800-554-0910</a>
            </p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>Email:</strong> <a href="mailto:Orders@BSTrading.com" style={{ color: theme.colors.primary }}>Orders@BSTrading.com</a>
            </p>
            <p style={{ ...paragraphStyles, marginBottom: 0 }}>
              <strong>Hours:</strong> Monday - Friday, 8:00 AM - 5:00 PM CST
            </p>
          </div>
        </div>
      </Container>
    </div>
    </FooterPageContent>
  );
};

export default ShippingPolicyPage;

